import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Plus, ShoppingBag, Gift, RefreshCw, Trash2, Eye, CheckCircle2, ChevronRight, Clock3, BarChart3 } from "lucide-react";
import { PageHeader } from "@/components/shared/PageHeader";
import { StatCard } from "@/components/shared/StatCard";
import {
  useOrders,
  useDeleteOrder,
  OrderStatusBadge,
  OrderTypeBadge,
  OrderFiltersBar,
} from "@/features/orders";
import { esIngreso } from "@cold-crew/shared";
import type { OrderFilters, Order } from "@cold-crew/shared";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/pedidos/")({
  component: PedidosPage,
});

function fmt(n: number) {
  return new Intl.NumberFormat("es-MX", {
    style: "currency", currency: "MXN", maximumFractionDigits: 2,
  }).format(n);
}

function fmtDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-MX", {
    day: "numeric", month: "short", year: "numeric",
  });
}

function PagadoChip({ pagado }: { pagado: boolean }) {
  return pagado
    ? <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-700 px-1.5 py-0.5 text-[10px] font-semibold">Pagado</span>
    : <span className="inline-flex items-center rounded-full bg-muted text-muted-foreground px-1.5 py-0.5 text-[10px] font-medium">Sin pagar</span>;
}

function PedidosPage() {
  const [filters, setFilters] = useState<Partial<OrderFilters>>({ page: 1, limit: 20 });

  const { data, isLoading, isError, refetch, isFetching } = useOrders(filters);
  const deleteOrder = useDeleteOrder();

  const orders: Order[] = data?.data       ?? [];
  const total            = data?.total      ?? 0;
  const totalPages       = data?.totalPages ?? 1;
  const page             = data?.page       ?? 1;

  const pending = orders.filter((o) => o.status === "pendiente").length;
  const pagados = orders.filter((o) => o.pagado).length;
  const regalos = orders.filter((o) => o.tipo_pedido === "regalo").length;
  const revenue = orders.filter((o) => esIngreso(o)).reduce((s, o) => s + o.total, 0);

  async function handleDelete(id: number) {
    if (!confirm("¿Eliminar este pedido? La acción es reversible.")) return;
    await deleteOrder.mutateAsync(id);
  }

  return (
    <div className="p-6 space-y-5 bg-[#f4f1ea] min-h-full">

      {/* Header — igual que antes */}
      <PageHeader
        title="Pedidos"
        description={`${total} pedido${total !== 1 ? "s" : ""} en total`}
        actions={
          <div className="flex items-center gap-2">
            <button onClick={() => refetch()} disabled={isFetching}
              className="flex size-10 items-center justify-center rounded-xl border border-[#ddd8cf] bg-white text-[#4f4a44] hover:bg-[#f8f6f1] transition-colors disabled:opacity-50" title="Actualizar">
              <RefreshCw className={cn("size-4", isFetching && "animate-spin")} />
            </button>
            {/* Botón Nuevo pedido — mismo degradado del Hero/Sidebar */}
            <Link to="/pedidos/nuevo"
              style={{ background: "#090909" }}
              className="flex h-10 items-center gap-2 rounded-xl px-4 text-sm font-bold text-white hover:opacity-90 transition-opacity">
              <Plus className="size-4" /> Nuevo pedido
            </Link>
          </div>
        }
      />

      {/* Stats — Opción 1: limpia y minimalista */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[
          { label: "Pendientes", value: String(pending), icon: Clock3, box: "bg-[#fff0e7]", iconColor: "text-[#f47b2a]" },
          { label: "Pagados", value: String(pagados), icon: CheckCircle2, box: "bg-[#e8f6ed]", iconColor: "text-[#176c4f]" },
          { label: "Regalos", value: String(regalos), icon: Gift, box: "bg-[#f2e9fb]", iconColor: "text-[#8b4ac6]" },
          { label: "Facturado", value: fmt(revenue), icon: BarChart3, box: "bg-[#fff0e7]", iconColor: "text-[#f47b2a]" },
        ].map((stat) => {
          const Icon = stat.icon;
          return (
            <div
              key={stat.label}
              className="flex min-h-[92px] items-center gap-4 rounded-2xl border border-[#ded9cf] bg-white px-5 py-4"
            >
              <div className={cn("flex size-11 shrink-0 items-center justify-center rounded-full", stat.box)}>
                <Icon className={cn("size-5", stat.iconColor)} strokeWidth={2} />
              </div>
              <div className={cn(
  "min-w-0",
  stat.label === "Facturado" && "pr-2 sm:pr-0"
)}>
  <p className="text-[12px] font-semibold text-[#77726a]">{stat.label}</p>
                <p
  className={cn(
    "mt-1 whitespace-nowrap font-black leading-none tracking-[-0.035em] text-[#171614]",
    stat.label === "Facturado"
      ? "text-[19px] sm:text-[24px]"
      : "text-[24px]"
  )}
>
  {stat.value}
</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filtros */}
      <OrderFiltersBar filters={filters} onChange={setFilters} />

      {/* ══ MÓVIL: lista de tarjetas (nuevo) ══ */}
      <div className="sm:hidden rounded-lg border border-border overflow-hidden bg-card">
        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground text-sm gap-2">
            <span className="size-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            Cargando pedidos…
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <p className="text-sm text-destructive">Error al cargar pedidos.</p>
            <button onClick={() => refetch()} className="text-sm text-primary hover:underline">Reintentar</button>
          </div>
        ) : orders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-2 text-muted-foreground">
            <ShoppingBag className="size-8 opacity-40" strokeWidth={1.25} />
            <p className="text-sm">Sin pedidos</p>
            {(filters.search || filters.status || filters.tipo_pedido) && (
              <button onClick={() => setFilters({ page: 1, limit: 20 })} className="text-xs text-primary hover:underline">Limpiar filtros</button>
            )}
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {orders.map((order) => (
              <li key={order.id} className={cn(
                "flex items-center gap-3 px-4 py-3.5",
                order.tipo_pedido === "regalo" && "bg-purple-50/30"
              )}>
                {/* Ícono */}
                <div className={cn(
                  "shrink-0 size-9 rounded-xl flex items-center justify-center",
                  order.tipo_pedido === "regalo" ? "bg-purple-100" : "bg-primary/10"
                )}>
                  {order.tipo_pedido === "regalo"
                    ? <Gift className="size-4 text-purple-600" />
                    : <ShoppingBag className="size-4 text-primary" />}
                </div>
                {/* Info — tap para detalle */}
                <Link to="/pedidos/$pedidoId" params={{ pedidoId: String(order.id) }}
                  className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-semibold text-sm text-foreground truncate max-w-[140px]">{order.cliente_nombre}</span>
                    <PagadoChip pagado={order.pagado} />
                  </div>
                  {order.detalles && order.detalles.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(order.detalles as any[]).map((d, i) => (
                        <span key={i} className="text-[11px] font-bold bg-primary/10 text-primary rounded-md px-1.5 py-0.5">
                          {d.cantidad}× {d.producto_nombre ?? `#${d.producto_id}`}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                    <OrderStatusBadge status={order.status} />
                    <span className="text-[11px] text-muted-foreground">{fmtDate(order.fecha_pedido)}</span>
                  </div>
                  <span className="text-[10px] font-mono text-muted-foreground/60">{order.numero_pedido}</span>
                </Link>
                {/* Total */}
                <Link to="/pedidos/$pedidoId" params={{ pedidoId: String(order.id) }}
                  className="shrink-0 text-right">
                  <p className={cn("font-bold text-sm tabular-nums",
                    order.tipo_pedido === "regalo" ? "text-purple-600" : "text-foreground")}>
                    {fmt(order.total)}
                  </p>
                  <p className="text-[10px] text-muted-foreground capitalize">{order.metodo_pago === "n/a" ? "N/A" : order.metodo_pago}</p>
                </Link>
                {/* Acciones */}
                <div className="shrink-0 flex items-center gap-0.5">
                  <ChevronRight className="size-4 text-muted-foreground/30" />
                  <button onClick={(e) => { e.stopPropagation(); handleDelete(order.id); }}
                    disabled={deleteOrder.isPending}
                    className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors disabled:opacity-40">
                    <Trash2 className="size-3.5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ══ ESCRITORIO: tabla original ══ */}
      <div className="hidden sm:block overflow-hidden rounded-2xl border border-[#ded9cf] bg-white">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground text-sm gap-2">
            <span className="size-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            Cargando pedidos…
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <p className="text-sm text-destructive">Error al cargar pedidos.</p>
            <button onClick={() => refetch()} className="text-sm text-primary hover:underline">Reintentar</button>
          </div>
        ) : orders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-2 text-muted-foreground">
            <ShoppingBag className="size-8 opacity-40" strokeWidth={1.25} />
            <p className="text-sm">Sin pedidos</p>
            {(filters.search || filters.status || filters.tipo_pedido) && (
              <button onClick={() => setFilters({ page: 1, limit: 20 })} className="text-xs text-primary hover:underline">Limpiar filtros</button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              {/* Encabezado de tabla — mismo degradado del Hero/Sidebar, texto blanco */}
              <thead>
                <tr className="bg-[#090909]">
                  {["# Pedido","Tipo","Cliente","Productos","Fecha pedido","Entrega","Estado","Pagado","Pago","Total",""].map((h) => (
                    <th key={h} className="px-4 py-3.5 text-left text-[11px] font-bold text-white/80 uppercase tracking-[0.04em] whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e8e3da]">
                {orders.map((order) => (
                  <tr key={order.id} className={cn("hover:bg-[#faf8f4] transition-colors group",
                    order.tipo_pedido === "regalo" && "bg-purple-50/30 dark:bg-purple-950/10")}>
                    <td className="px-4 py-3.5 font-mono text-xs text-[#77726a] whitespace-nowrap">{order.numero_pedido}</td>
                    <td className="px-4 py-3"><OrderTypeBadge tipo={order.tipo_pedido} /></td>
                    <td className="px-4 py-3 font-medium text-foreground max-w-[160px] truncate">
                      {order.cliente_nombre}
                      {order.cliente_contacto && <span className="block text-xs text-muted-foreground font-normal truncate">{order.cliente_contacto}</span>}
                    </td>
                    <td className="px-4 py-3 max-w-[180px]">
                      {order.detalles && order.detalles.length > 0 ? (
                        <div className="space-y-0.5">
                          {order.detalles.map((d: any, i: number) => (
                            <span key={i} className="block text-xs font-semibold text-foreground">
                              {d.cantidad}× {d.producto_nombre ?? `#${d.producto_id}`}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{fmtDate(order.fecha_pedido)}</td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{fmtDate(order.fecha_entrega)}</td>
                    <td className="px-4 py-3"><OrderStatusBadge status={order.status} /></td>
                    <td className="px-4 py-3">
                      {order.pagado
                        ? <span className="inline-flex items-center rounded-full bg-[#dff3e8] px-2.5 py-1 text-[11px] font-semibold text-[#176c4f]">Pagado</span>
                        : <span className="inline-flex items-center rounded-full bg-[#efede8] px-2.5 py-1 text-[11px] font-medium text-[#615d56]">No pagado</span>}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground capitalize whitespace-nowrap">
                      {order.metodo_pago === "n/a" ? <span className="text-purple-600 dark:text-purple-400 text-xs">N/A</span> : order.metodo_pago}
                    </td>
                    <td className="px-4 py-3 tabular-nums font-semibold whitespace-nowrap">
                      <span className={order.tipo_pedido === "regalo" ? "text-purple-600 dark:text-purple-400" : "text-foreground"}>{fmt(order.total)}</span>
                      {order.tipo_pedido === "regalo" && <span className="block text-xs text-purple-500 font-normal">no ingreso</span>}
                      {order.tipo_pedido === "venta" && order.descuento > 0 && <span className="block text-xs text-emerald-600 font-normal">-{fmt(order.descuento)} desc.</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Link to="/pedidos/$pedidoId" params={{ pedidoId: String(order.id) }}
                          className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" title="Ver detalle">
                          <Eye className="size-3.5" />
                        </Link>
                        <button onClick={() => handleDelete(order.id)} disabled={deleteOrder.isPending}
                          className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors disabled:opacity-40" title="Eliminar">
                          <Trash2 className="size-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Paginación — igual que antes */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>Página {page} de {totalPages} — {total} registros</span>
          <div className="flex items-center gap-2">
            <button onClick={() => setFilters((f) => ({ ...f, page: (f.page ?? 1) - 1 }))}
              disabled={page <= 1}
              className="px-3 py-1.5 rounded-md border border-border hover:bg-muted transition-colors disabled:opacity-40">‹ Anterior</button>
            <button onClick={() => setFilters((f) => ({ ...f, page: (f.page ?? 1) + 1 }))}
              disabled={page >= totalPages}
              className="px-3 py-1.5 rounded-md border border-border hover:bg-muted transition-colors disabled:opacity-40">Siguiente ›</button>
          </div>
        </div>
      )}
    </div>
  );
}

